import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const FALLBACK_LOCALE =
    (typeof navigator !== "undefined" && (navigator.language || navigator.userLanguage)) || "en-US";

const LOCALE_ALIASES = {
    en: "en-US",
    en_US: "en-US",
    "en-US": "en-US",
    ru: "ru-RU",
    ru_RU: "ru-RU",
    "ru-RU": "ru-RU",
};

const normalizeDateInput = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const asDate = new Date(value);
    return Number.isNaN(asDate.getTime()) ? null : asDate;
};

const sanitizeOptions = (options = {}) =>
    Object.entries(options).reduce((acc, [key, optionValue]) => {
        if (optionValue !== undefined) {
            acc[key] = optionValue;
        }
        return acc;
    }, {});

const canonicalizeLocale = (locale) => {
    if (!locale) {
        return null;
    }
    if (typeof Intl !== "undefined" && typeof Intl.getCanonicalLocales === "function") {
        try {
            const [canonical] = Intl.getCanonicalLocales(locale);
            if (canonical) {
                return canonical;
            }
        } catch (error) {
            // Ignore invalid locale errors and fall back below
        }
    }
    return locale;
};

const ensureSupportedLocale = (locale) => {
    const canonical = canonicalizeLocale(locale);
    if (!canonical) {
        return null;
    }
    try {
        // Verify the locale so Intl.DateTimeFormat won't throw later
        new Intl.DateTimeFormat(canonical);
        return canonical;
    } catch (error) {
        return null;
    }
};

const resolveLocale = (rawLocale) => {
    if (!rawLocale) {
        return FALLBACK_LOCALE;
    }
    const trimmed = rawLocale.trim();
    const underscoreLocale = trimmed.replace(/-/g, "_");
    if (LOCALE_ALIASES[underscoreLocale]) {
        return LOCALE_ALIASES[underscoreLocale];
    }
    const base = underscoreLocale.split("_")[0];
    if (LOCALE_ALIASES[base]) {
        return LOCALE_ALIASES[base];
    }
    const bcpLocale = ensureSupportedLocale(trimmed.replace(/_/g, "-"));
    if (bcpLocale) {
        return bcpLocale;
    }
    const fallbackBase = ensureSupportedLocale(base);
    if (fallbackBase) {
        return fallbackBase;
    }
    return FALLBACK_LOCALE;
};

export default function useLocaleDateFormatter() {
    const { i18n } = useTranslation();

    const locale = useMemo(
        () => resolveLocale(i18n.resolvedLanguage || i18n.language || FALLBACK_LOCALE),
        [i18n.language, i18n.resolvedLanguage]
    );

    const getFormatter = useMemo(() => {
        const formatterCache = new Map();
        return (options) => {
            const safeOptions = sanitizeOptions(options);
            const cacheKey = JSON.stringify(safeOptions);
            if (!formatterCache.has(cacheKey)) {
                formatterCache.set(cacheKey, new Intl.DateTimeFormat(locale, safeOptions));
            }
            return formatterCache.get(cacheKey);
        };
    }, [locale]);

    const formatWithOptions = useCallback(
        (dateInput, formatOptions) => {
            const date = normalizeDateInput(dateInput);
            if (!date) {
                return "";
            }
            try {
                return getFormatter(formatOptions).format(date);
            } catch (error) {
                console.warn("Date formatting failed, falling back to ISO string", error);
                return date.toISOString();
            }
        },
        [getFormatter]
    );

    const formatDateTime = useCallback(
        (dateInput, options = {}) =>
            formatWithOptions(dateInput, {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                ...options,
            }),
        [formatWithOptions]
    );

    const formatDate = useCallback(
        (dateInput, options = {}) =>
            formatWithOptions(dateInput, {
                year: "numeric",
                month: "long",
                day: "numeric",
                ...options,
            }),
        [formatWithOptions]
    );

    const formatMonthYear = useCallback(
        (dateInput, options = {}) =>
            formatWithOptions(dateInput, {
                month: "long",
                year: "numeric",
                ...options,
            }),
        [formatWithOptions]
    );

    const formatTime = useCallback(
        (dateInput, options = {}) => {
            const date = normalizeDateInput(dateInput);
            if (!date) {
                return "";
            }

            const isEnglishLocale = /^en\b/i.test(locale || "");
            const baseOptions = isEnglishLocale
                ? { hour: "numeric", minute: "2-digit", hour12: true }
                : { hour: "2-digit", minute: "2-digit", hour12: false };
            const mergedOptions = { ...baseOptions, ...options };

            try {
                const formatter = getFormatter(mergedOptions);
                if (isEnglishLocale && typeof formatter.formatToParts === "function") {
                    const parts = formatter.formatToParts(date);
                    const hourValue = parts.find((part) => part.type === "hour")?.value ?? "";
                    const minuteValue = parts.find((part) => part.type === "minute")?.value ?? "";
                    const periodValue = parts.find((part) => part.type === "dayPeriod")?.value ?? "";
                    if (hourValue && minuteValue) {
                        const periodSuffix = periodValue ? periodValue.toUpperCase().replace(/\s+/g, "") : "";
                        return `${hourValue}:${minuteValue}${periodSuffix}`;
                    }
                }
                const formatted = formatter.format(date);
                return isEnglishLocale ? formatted.replace(/\s+/g, "") : formatted;
            } catch (error) {
                console.warn("Time formatting failed, falling back to locale string", error);
                return date.toLocaleTimeString();
            }
        },
        [getFormatter, locale]
    );

    return {
        locale,
        formatDateTime,
        formatDate,
        formatMonthYear,
        formatTime,
    };
}
